import React, {useState} from 'react';
import root from 'window-or-global';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import styled from 'styled-components';
import {AccentColorOptions, AccentColorProps} from 'epic-ue-theme';
import {Typography} from '../typography';
import Grid from '@material-ui/core/Grid';
import {Eyebrow} from '../eyebrow';
import {LineTextBlock} from '../text/LineTextBlock';
import {VerticalSpacer} from '../spacer/VerticalSpacer';
import {Button} from '../buttons/Button';
import {subNavHeight} from '../subnav/Subnav.config';

const AccordionWrap = styled(MuiAccordion)`
    && {
        background-color: unset;
        box-shadow: unset;
        padding: 0;
        position: unset;
        border-bottom: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.neutral.steel, p.theme.palette.neutral.silver)}
            1px solid;
        &.expanded {
            margin: 0;
        }
        &:before {
            display: none;
        }
    }
`;

const AccordionDetailsWrap = styled(MuiAccordionDetails)`
    && {
        padding: 0 0 32px 0;
        ${(p) => p.theme.breakpoints.down('sm')} {
            padding: 0 54px 16px 0;
        }
        ${(p) => p.theme.breakpoints.up('md')} {
            padding-right: 104px;
        }
        .paragraph {
            color: ${(p) => p.theme.palette.text.secondary};
        }
    }
`;

const AccordionSummaryWrap = styled(MuiAccordionSummary)`
    &&& {
        min-height: 0;
    }
    && {
        margin: 32px 0;
        padding: 0;
        ${(p) => p.theme.breakpoints.down('sm')} {
            margin: 16px 0;
        }
        .content {
            margin: 0 24px 0 0;
            ${(p) => p.theme.breakpoints.down('sm')} {
                margin: 0 16px 0 0;
            }
        }
        .edge-end {
            padding: 0;
            margin-right: 13px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                margin-right: 8px;
            }
            transform: rotate(0deg);
            svg {
                position: absolute;
                font-size: 1.7145em;
                ${(p) => p.theme.breakpoints.down('sm')} {
                    font-size: 1.143em;
                }
            }
        }
        &:focus-visible {
            outline: 1px solid ${(p) => p.theme.palette.misc.outline};
            outline-offset: 2px;
            background: unset;
        }
    }
`;

const Styled = styled.div<{titleAboveItems: boolean}>`
    ${(p) => p.theme.breakpoints.up('md')} {
        border-top: ${(p) =>
                p.titleAboveItems
                    ? 'unset'
                    : p.theme.ifLight(
                          p,
                          p.theme.palette.neutral.steel,
                          p.theme.palette.neutral.silver
                      )}
            1px solid;
    }
    .expand-all {
        color: ${(p) => p.theme.palette.text.primary};
    }
    .ue-accordion-group {
        padding-top: 24px;
        ${(p) => p.theme.breakpoints.up('md')} {
            padding-top: 40px;
        }
        .eyebrow-wrapper {
            margin-bottom: 12px;
            ${(p) => p.theme.breakpoints.up('md')} {
                max-width: 180px;
            }
            ${(p) => p.theme.breakpoints.up('lg')} {
                max-width: 220px;
            }
            ${(p) => p.theme.breakpoints.up('xl')} {
                max-width: 260px;
            }
        }
        border-bottom: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.neutral.steel, p.theme.palette.neutral.silver)}
            1px solid;
    }
    .ue-accordion-item {
        div:only-child {
            min-height: calc(100% - 1px);
        }
    }

    .heading-title {
        border-bottom: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.neutral.steel, p.theme.palette.neutral.silver)}
            1px solid;
    }
`;

const ToggleIcon = styled.div<{color: AccentColorOptions}>`
    position: relative;
    overflow: hidden;
    width: 25px;
    height: 25px;

    &:before,
    &:after {
        position: absolute;
        content: '';
        background-color: ${(p) => p.theme.palette.accent[p.color]};
        transition: all 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
    }

    &:before {
        width: 25px;
        height: 3px;
        top: 11px;
        left: 0;
    }

    &:after {
        width: 3px;
        height: 25px;
        top: 0;
        left: 11px;
        transform-origin: center;
    }

    &.minus {
        &:after {
            height: 0;
            opacity: 0;
        }
    }
`;

export interface AccordionProps extends AccentColorProps {
    items?: AccordionItemProps[];
    defaultExpanded?: boolean;
    titleAboveItems?: boolean;
    closeAllText?: string;
    openAllText?: string;
    accentColor?: AccentColorOptions;
}

export interface AccordionItemProps {
    title?: string;
    description?: string;
    customDescriptionNode?: React.ReactNode;
    items?: AccordionItemProps[];
    onChange?: (arg: number | undefined | null, arg2?: string | undefined) => void;
    sectionTitle?: string;
    itemIndex?: number;
    expanded?: boolean;
    id?: string;
    childPageSlug?: string;
    isTopResult?: boolean;
}

const AccordionItem = ({
    title,
    description,
    customDescriptionNode,
    accentColor,
    onChange,
    sectionTitle,
    itemIndex,
    expanded
}: AccordionItemProps & AccentColorProps) => {
    const handleChange = () => {
        if (expanded) {
            onChange && onChange(null, sectionTitle);
        } else {
            onChange && onChange(itemIndex, sectionTitle);
        }
    };

    return (
        <AccordionWrap
            square={true}
            className={`itemIndex-${itemIndex}-${sectionTitle}`}
            classes={{expanded: 'expanded'}}
            expanded={expanded}
            TransitionProps={{
                timeout: {enter: 200, exit: 150}
            }}
            onChange={handleChange}>
            <AccordionSummaryWrap
                classes={{content: 'content'}}
                expandIcon={
                    expanded ? (
                        <ToggleIcon className="minus" color={accentColor || 'sky'} />
                    ) : (
                        <ToggleIcon className="plus" color={accentColor || 'sky'} />
                    )
                }
                IconButtonProps={{classes: {edgeEnd: 'edge-end'}}}>
                <Typography variant="h3" big>
                    {title}
                </Typography>
            </AccordionSummaryWrap>
            {customDescriptionNode ? (
                customDescriptionNode
            ) : (
                <AccordionDetailsWrap>
                    <Typography variant="p" accentColor={accentColor}>
                        {description}
                    </Typography>
                </AccordionDetailsWrap>
            )}
        </AccordionWrap>
    );
};

export const Accordion = ({
    items = [],
    accentColor,
    defaultExpanded,
    titleAboveItems = false,
    closeAllText,
    openAllText
}: AccordionProps): JSX.Element => {
    const [expandedItem, setExpandedItem] = useState(null);
    const [expandedSection, setExpandedSection] = useState(null);
    const [defaultExpand, setDefaultExpand] = useState(defaultExpanded);

    const scrollToItem = (newExpandedItem, sectionTitle) => {
        if (root.__server_side_render) return;
        const el = root.document.getElementsByClassName(
            `itemIndex-${newExpandedItem}-${sectionTitle}`
        );
        if (el) {
            let element = el[0];
            let offsetTop = 0;
            while (element) {
                offsetTop += element.offsetTop;
                element = element.offsetParent;
            }
            root.scrollTopSkip = location.pathname; //prevent ScrollToTop from resetting position for this page
            root.scrollTo({left: 0, top: offsetTop - subNavHeight, behavior: 'smooth'});
        }
    };

    const handleChange = (newExpandedItem, sectionTitle) => {
        if (sectionTitle === expandedSection) {
            setExpandedItem(null);
            setExpandedSection(null);
        } else if (newExpandedItem === null) {
            setExpandedItem(newExpandedItem);
        } else {
            setExpandedItem(newExpandedItem);
            //after css transitons done
            setTimeout(() => {
                scrollToItem(newExpandedItem, sectionTitle);
            }, 300);
        }
        setDefaultExpand(false);
    };

    const closeAllItemsInSection = (e, sectionTitle) => {
        e.preventDefault();
        setExpandedSection(null);
    };
    const expandAllItemsInSection = (e, sectionTitle) => {
        e.preventDefault();
        setExpandedSection(sectionTitle);
    };

    let totalItemIndex = 0;
    const prepareItems = (items: AccordionItemProps[], sectionTitle?: string): JSX.Element[] => {
        const elements: JSX.Element[] = [];
        ((Array.isArray(items) && items) || []).forEach((item, index) => {
            if (!item.items) {
                elements.push(
                    <AccordionItem
                        key={`${item.title}-${index}`}
                        accentColor={accentColor}
                        onChange={handleChange}
                        sectionTitle={sectionTitle}
                        itemIndex={totalItemIndex}
                        expanded={
                            defaultExpand
                                ? true
                                : expandedSection === sectionTitle
                                ? true
                                : totalItemIndex === expandedItem
                        }
                        title={item.title}
                        description={item.description}
                        customDescriptionNode={item.customDescriptionNode}
                    />
                );
                totalItemIndex++;
            } else {
                titleAboveItems
                    ? elements.push(
                          <React.Fragment key={`grid-title-above-${item.title}-${index}`}>
                              <Grid
                                  className={`sectionId-${index} heading-title`}
                                  container
                                  justifyContent="space-between"
                                  alignItems="flex-end"
                                  direction="row"
                                  spacing={0}>
                                  <Grid item xs={12} sm={true}>
                                      <LineTextBlock
                                          headline={item.title}
                                          accentColor={accentColor}
                                          disableAnim
                                          hideHR
                                      />
                                  </Grid>
                                  <Grid item xs={12} sm={'auto'}>
                                      <Button
                                          onClick={(e) => {
                                              expandedSection === item.title
                                                  ? closeAllItemsInSection(e, item.title)
                                                  : expandAllItemsInSection(e, item.title);
                                          }}
                                          type={'tertiary'}
                                          color={accentColor}
                                          className={'expand-all'}>
                                          {expandedSection === item.title
                                              ? closeAllText || 'Close All'
                                              : openAllText || 'Open All'}
                                      </Button>
                                      <VerticalSpacer
                                          variant="custom"
                                          spacing={3}
                                          downSmSpacing={2}
                                      />
                                  </Grid>
                              </Grid>
                              <Grid
                                  key={index}
                                  container
                                  justifyContent="flex-start"
                                  alignItems="stretch">
                                  <Grid item xs={12} className="ue-accordion-item">
                                      {prepareItems(item.items, item.title)}
                                  </Grid>
                              </Grid>
                              <VerticalSpacer variant="section" />
                          </React.Fragment>
                      )
                    : elements.push(
                          <Grid
                              key={index}
                              container
                              justifyContent="flex-start"
                              alignItems="stretch">
                              <Grid item md={3} sm={12} xs={12} className="ue-accordion-group">
                                  {item.title && <Eyebrow text={item.title} />}
                              </Grid>
                              <Grid item md={9} sm={12} xs={12} className="ue-accordion-item">
                                  {prepareItems(item.items)}
                              </Grid>
                          </Grid>
                      );
            }
        });
        return elements;
    };
    const elements = prepareItems(items);
    return <Styled titleAboveItems={titleAboveItems}>{elements}</Styled>;
};
